class BasicHeader extends HTMLElement {
  constructor() {
    super();

    this._resizeObserver = null;
    this._heightRaf = null;
  }

  get headerSection() {
    return this.closest('.header-section') || document.querySelector('.header-section');
  }

  get headerNavigation() {
    return this.querySelector('.header__bottom');
  }

  get navigationToggleButton() {
    return this.querySelector('.toggle-navigation-button');
  }

  get enableTransparent() {
    return this.dataset.enableTransparent === 'true';
  }

  connectedCallback() {
    this.init();

    if (window.ResizeObserver && !this._resizeObserver) {
      this._resizeObserver = new ResizeObserver(this.setHeight.bind(this));
      this._resizeObserver.observe(this);
    }

    if (window.Shopify && Shopify.designMode) {
      const section = this.closest('.shopify-section');

      if (section && !this._pvDesignModeListenersAdded) {
        this._pvDesignModeListenersAdded = true;

        section.addEventListener('shopify:section:load', this.init.bind(this));
        section.addEventListener('shopify:section:unload', this.init.bind(this));
        section.addEventListener('shopify:section:reorder', this.init.bind(this));
      }
    }
  }

  disconnectedCallback() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }

    if (this._heightRaf) {
      cancelAnimationFrame(this._heightRaf);
      this._heightRaf = null;
    }
  }

  init() {
    if (window.FoxTheme && FoxTheme.delayUntilInteraction) {
      new FoxTheme.delayUntilInteraction(this.setHeight.bind(this));
    } else {
      this.setHeight();
    }

    if (this.enableTransparent && this.headerSection) {
      this.headerSection.classList.add('header-transparent');
    }
  }

  calculateHeaderGroupHeight() {
    const headerGroups = document.querySelectorAll('.shopify-section-group-header-group');
    let totalHeight = 0;

    headerGroups.forEach((section) => {
      totalHeight += section.offsetHeight || 0;
    });

    document.documentElement.style.setProperty('--header-group-height', `${totalHeight}px`);

    return totalHeight;
  }

  setHeight() {
    if (this._heightRaf) {
      cancelAnimationFrame(this._heightRaf);
    }

    this._heightRaf = requestAnimationFrame(() => {
      this._heightRaf = null;

      const offsetHeight = Math.round(this.offsetHeight || 0);
      const offsetTop = Math.round(this.parentElement ? this.parentElement.offsetTop || 0 : 0);
      const headerNavigation = this.headerNavigation;
      const offsetNavigationHeight = Math.round(headerNavigation ? headerNavigation.offsetHeight || 0 : 0);

      document.documentElement.style.setProperty('--header-height', `${offsetHeight}px`);
      document.documentElement.style.setProperty('--header-offset-top', `${offsetTop}px`);
      document.documentElement.style.setProperty('--header-navigation-height', `${offsetNavigationHeight}px`);

      this.calculateHeaderGroupHeight();
    });
  }
}

if (!customElements.get('basic-header')) {
  customElements.define('basic-header', BasicHeader, { extends: 'header' });
}

class StickyHeader extends BasicHeader {
  constructor() {
    super();

    this.classes = {
      pinned: 'header-pinned',
      headerScrolled: 'header-scrolled',
      show: 'is-show',
      hide: 'is-hide',
      headerSticky: 'header-sticky',
      isHideNav: 'is-hide-nav',
    };

    this.navigationManuallyToggled = false;

    this.currentScrollTop = 0;
    this.lastScrollPos = 0;
    this.latestScrollTop = 0;

    this.scrollThreshold = 200;
    this.scrollDirection = 'none';
    this.scrollDistance = 0;

    this.headerBounds = null;
    this.firstScrollTop = 0;

    this._scrollTicking = false;
    this._scrollListenerAdded = false;

    this.handleScroll = this.handleScroll.bind(this);
    this.handleNavigationToggle = this.handleNavigationToggle.bind(this);
    this.refreshHeaderBounds = this.refreshHeaderBounds.bind(this);
  }

  get isAlwaysSticky() {
    return this.dataset.stickyType === 'always';
  }

  get collapseOnScroll() {
    return this.dataset.collapseOnScroll === 'true';
  }

  connectedCallback() {
    super.connectedCallback();

    this.firstScrollTop = window.scrollY || 0;
    this.currentScrollTop = this.firstScrollTop;
    this.latestScrollTop = this.firstScrollTop;

    this.refreshHeaderBounds();
    this.initStickyHeader();

    if (this.collapseOnScroll && this.navigationToggleButton && !this._navigationToggleListenerAdded) {
      this._navigationToggleListenerAdded = true;
      this.navigationToggleButton.addEventListener('click', this.handleNavigationToggle);
    }

    if (!this._resizeListenerAdded) {
      this._resizeListenerAdded = true;
      window.addEventListener('resize', this.refreshHeaderBounds, { passive: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this._scrollListenerAdded) {
      this._scrollListenerAdded = false;
      window.removeEventListener('scroll', this.handleScroll);
    }

    if (this._navigationToggleListenerAdded && this.navigationToggleButton) {
      this._navigationToggleListenerAdded = false;
      this.navigationToggleButton.removeEventListener('click', this.handleNavigationToggle);
    }

    if (this._resizeListenerAdded) {
      this._resizeListenerAdded = false;
      window.removeEventListener('resize', this.refreshHeaderBounds);
    }
  }

  refreshHeaderBounds() {
    const headerSection = this.headerSection;

    if (!headerSection) {
      this.headerBounds = null;
      return;
    }

    this.headerBounds = headerSection.getBoundingClientRect();
    this.firstScrollTop = window.scrollY || 0;

    this.setHeight();
  }

  initStickyHeader() {
    const headerSection = this.headerSection;

    if (!headerSection) return;

    headerSection.classList.add(this.classes.headerSticky);
    headerSection.dataset.stickyType = this.dataset.stickyType;

    if (!this._scrollListenerAdded) {
      this._scrollListenerAdded = true;
      window.addEventListener('scroll', this.handleScroll, { passive: true });
    }
  }

  handleNavigationToggle(event) {
    event.preventDefault();

    if (!this.headerNavigation || !this.navigationToggleButton) return;

    this.navigationManuallyToggled = true;

    const isHidden = this.headerNavigation.classList.contains(this.classes.hide);

    setTimeout(
      () => {
        document.body.classList.toggle(this.classes.isHideNav, !isHidden);
      },
      isHidden ? 0 : 250
    );

    this.headerNavigation.classList.toggle(this.classes.hide, !isHidden);
    this.headerNavigation.classList.toggle(this.classes.show, isHidden);
    this.navigationToggleButton.setAttribute('aria-expanded', String(isHidden));

    setTimeout(() => {
      this.navigationManuallyToggled = false;
    }, 1000);
  }

  handleScroll() {
    this.latestScrollTop = window.scrollY || 0;

    if (this._scrollTicking) return;

    this._scrollTicking = true;

    requestAnimationFrame(() => {
      this._scrollTicking = false;

      const scrollTop = this.latestScrollTop;
      const headerSection = this.headerSection;

      if (!headerSection || !this.headerBounds) return;

      const headerBoundsTop = this.headerBounds.top + this.firstScrollTop;
      const headerBoundsBottom = this.headerBounds.bottom + this.firstScrollTop;

      this.updateScrollMetrics(scrollTop);

      const isScrolledPastHeader = scrollTop > headerBoundsTop;

      if (isScrolledPastHeader) {
        this.handleScrolledPastHeader(scrollTop, headerBoundsBottom);
      } else {
        this.handleScrolledBeforeHeader();
      }

      this.currentScrollTop = scrollTop;
    });
  }

  updateScrollMetrics(scrollTop) {
    const newDirection = scrollTop > this.currentScrollTop ? 'down' : 'up';

    if (newDirection !== this.scrollDirection) {
      this.scrollDistance = 0;
      this.scrollDirection = newDirection;
    } else {
      this.scrollDistance += Math.abs(scrollTop - this.currentScrollTop);
    }
  }

  handleScrolledPastHeader(scrollTop, headerBoundsBottom) {
    const headerSection = this.headerSection;

    if (!headerSection) return;

    headerSection.classList.add(this.classes.headerScrolled);

    if (this.collapseOnScroll && this.navigationToggleButton && this.headerNavigation) {
      this.navigationToggleButton.classList.add(this.classes.show);

      if (!this.navigationManuallyToggled && !this.headerNavigation.classList.contains(this.classes.show)) {
        this.headerNavigation.classList.add(this.classes.hide);
        document.body.classList.add(this.classes.isHideNav);
      }
    }

    if (this.isAlwaysSticky) {
      document.body.classList.add(this.classes.pinned);
    } else {
      const isScrollingUp = this.scrollDirection === 'up';
      const isNearHeader = scrollTop < headerBoundsBottom + 100;
      const hasScrolledEnough = this.scrollDistance >= this.scrollThreshold;

      if (isScrollingUp || isNearHeader) {
        document.body.classList.add(this.classes.pinned);
      } else if (!this.navigationManuallyToggled && hasScrolledEnough) {
        document.body.classList.remove(this.classes.pinned);
      }
    }
  }

  handleScrolledBeforeHeader() {
    const headerSection = this.headerSection;

    if (!headerSection) return;

    headerSection.classList.remove(this.classes.headerScrolled);

    if (this.collapseOnScroll && this.navigationToggleButton && this.headerNavigation) {
      if (!this.navigationManuallyToggled) {
        document.body.classList.remove(this.classes.isHideNav);
        this.headerNavigation.classList.remove(this.classes.hide);
        this.headerNavigation.classList.remove(this.classes.show);
        this.navigationToggleButton.setAttribute('aria-expanded', 'false');
      }

      this.navigationToggleButton.classList.remove(this.classes.show);
    }

    if (this.isAlwaysSticky) {
      document.body.classList.remove(this.classes.pinned);
    }
  }
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader, { extends: 'header' });
}

const clearDropdownCount = new WeakMap();

class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super();

    this.classes = { bodyClass: 'has-dropdown-menu' };
    this.events = {
      handleAfterHide: 'menu:handleAfterHide',
      handleAfterShow: 'menu:handleAfterShow',
    };

    this.summaryElement = this.querySelector('summary') || this.firstElementChild;
    this.contentElement = this.lastElementChild;

    this._open = this.hasAttribute('open');

    this.handleSummaryClick = this.handleSummaryClick.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEscKeyPress = this.handleEscKeyPress.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.detectHover = this.detectHover.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);

    this.hoverEnterTimer = null;
    this.hoverLeaveTimer = null;

    if (this.summaryElement) {
      this.summaryElement.addEventListener('click', this.handleSummaryClick);

      if (this.trigger === 'hover') {
        this.summaryElement.addEventListener('focusin', (event) => {
          if (event.target === this.summaryElement) {
            this.open = true;
          }
        });

        this.summaryElement.addEventListener('focusout', (event) => {
          if (!this.contentElement || !this.contentElement.contains(event.relatedTarget)) {
            this.open = false;
          }
        });
      }
    }

    this.addEventListener('mouseenter', this.handleMouseEnter);
    this.addEventListener('mouseleave', this.handleMouseLeave);
  }

  disconnectedCallback() {
    if (this.summaryElement) {
      this.summaryElement.removeEventListener('click', this.handleSummaryClick);
    }

    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);

    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleEscKeyPress);
    document.removeEventListener('focusout', this.handleFocusOut);

    if (this.hoverEnterTimer) clearTimeout(this.hoverEnterTimer);
    if (this.hoverLeaveTimer) clearTimeout(this.hoverLeaveTimer);
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value;

      if (this.isConnected) {
        this.transition(value);
      } else if (value) {
        this.setAttribute('open', '');
      } else {
        this.removeAttribute('open');
      }
    }
  }

  get open() {
    return this._open;
  }

  get trigger() {
    if (window.FoxTheme && FoxTheme.config && FoxTheme.config.isTouch) {
      return 'click';
    }

    return this.getAttribute('trigger') || 'click';
  }

  get level() {
    return this.hasAttribute('level') ? this.getAttribute('level') : 'top';
  }

  handleMouseEnter() {
    if (this.hoverLeaveTimer) {
      clearTimeout(this.hoverLeaveTimer);
      this.hoverLeaveTimer = null;
    }

    this.hoverEnterTimer = setTimeout(() => {
      this.detectHover({ type: 'mouseenter' });
    }, 100);
  }

  handleMouseLeave() {
    if (this.hoverEnterTimer) {
      clearTimeout(this.hoverEnterTimer);
      this.hoverEnterTimer = null;
    }

    this.hoverLeaveTimer = setTimeout(() => {
      this.detectHover({ type: 'mouseleave' });
    }, 150);
  }

  handleSummaryClick(event) {
    event.preventDefault();

    const isTouch = window.FoxTheme && FoxTheme.config && FoxTheme.config.isTouch;

    if (!isTouch && this.trigger === 'hover' && this.summaryElement && this.summaryElement.hasAttribute('data-link')) {
      window.location.href = this.summaryElement.getAttribute('data-link');
    } else {
      this.open = !this.open;
    }
  }

  async transition(value) {
    if (!this.summaryElement || !this.contentElement) return;

    if (value) {
      const currentCount = clearDropdownCount.get(DetailsDropdown) || 0;
      clearDropdownCount.set(DetailsDropdown, currentCount + 1);

      document.body.classList.add(this.classes.bodyClass);

      this.setAttribute('open', '');
      this.summaryElement.setAttribute('open', '');

      setTimeout(() => {
        if (this.contentElement) {
          this.contentElement.setAttribute('open', '');
        }
      }, 100);

      document.addEventListener('click', this.handleOutsideClick);
      document.addEventListener('keydown', this.handleEscKeyPress);
      document.addEventListener('focusout', this.handleFocusOut);

      await this.showWithTransition();

      const menuProductList = this.querySelector('menu-product-list');
      if (menuProductList && typeof menuProductList.calcNavButtonsPosition === 'function') {
        menuProductList.calcNavButtonsPosition();
      }

      this.needsReverse();

      if (window.FoxTheme && FoxTheme.utils && FoxTheme.utils.waitForEvent) {
        return FoxTheme.utils.waitForEvent(this, this.events.handleAfterShow);
      }

      return;
    }

    const currentCount = clearDropdownCount.get(DetailsDropdown) || 0;
    const nextCount = Math.max(0, currentCount - 1);
    clearDropdownCount.set(DetailsDropdown, nextCount);

    document.body.classList.toggle(this.classes.bodyClass, nextCount > 0);

    this.summaryElement.removeAttribute('open');
    this.contentElement.removeAttribute('open');

    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleEscKeyPress);
    document.removeEventListener('focusout', this.handleFocusOut);

    await this.hideWithTransition();

    if (!this.open) {
      this.removeAttribute('open');
    }

    if (window.FoxTheme && FoxTheme.utils && FoxTheme.utils.waitForEvent) {
      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterHide);
    }
  }

  async showWithTransition() {
    if (!this.contentElement || !this.contentElement.firstElementChild) return;

    if (!window.FoxTheme || !FoxTheme.Motion) {
      this.contentElement.style.visibility = 'visible';
      this.contentElement.style.opacity = '1';
      return;
    }

    FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: [0, 1], visibility: 'visible' },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
        delay: FoxTheme.config.motionReduced ? 0 : 0.1,
      }
    );

    const translateY = this.level === 'top' ? '-105%' : '2rem';

    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      { transform: [`translateY(${translateY})`, 'translateY(0)'] },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  async hideWithTransition() {
    if (!this.contentElement || !this.contentElement.firstElementChild) return;

    if (!window.FoxTheme || !FoxTheme.Motion) {
      this.contentElement.style.visibility = 'hidden';
      this.contentElement.style.opacity = '0';
      return;
    }

    FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: 0, visibility: 'hidden' },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.3,
        easing: [0.7, 0, 0.2, 1],
      }
    );

    const translateY = this.level === 'top' ? '-105%' : '2rem';

    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      { transform: `translateY(${translateY})` },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  handleOutsideClick(event) {
    const isClickInside = this.contains(event.target);
    const isClickOnDetailsDropdown = event.target.closest('details') instanceof DetailsDropdown;

    if (!isClickInside && !isClickOnDetailsDropdown) {
      this.open = false;
    }
  }

  handleEscKeyPress(event) {
    if (event.code === 'Escape') {
      const targetMenu = event.target.closest('details[open]');

      if (targetMenu) {
        targetMenu.open = false;
      }
    }
  }

  handleFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  detectHover(event) {
    if (this.trigger === 'hover') {
      this.open = event.type === 'mouseenter';
    }
  }

  needsReverse() {
    if (!this.contentElement) return;

    const totalWidth = this.contentElement.offsetLeft + this.contentElement.clientWidth * 2;

    if (totalWidth > window.innerWidth) {
      this.contentElement.classList.add('needs-reverse');
    }
  }
}

if (!customElements.get('details-dropdown')) {
  customElements.define('details-dropdown', DetailsDropdown, { extends: 'details' });
}
clearDropdownCount.set(DetailsDropdown, 0);

class DetailsMega extends DetailsDropdown {
  constructor() {
    super();

    if (window.Shopify && Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => {
        this.open = true;
      });

      this.addEventListener('shopify:block:deselect', () => {
        this.open = false;
      });
    }
  }

  async showWithTransition() {
    if (!this.contentElement || !this.contentElement.firstElementChild) return;

    if (!window.FoxTheme || !FoxTheme.Motion) {
      this.contentElement.firstElementChild.style.visibility = 'visible';
      return;
    }

    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: 'visible',
        transform: ['translateY(-100%)', 'translateY(0)'],
      },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.5,
        easing: [0.39, 0.575, 0.565, 1.0],
      }
    ).finished;
  }

  async hideWithTransition() {
    if (!this.contentElement || !this.contentElement.firstElementChild) return;

    if (!window.FoxTheme || !FoxTheme.Motion) {
      this.contentElement.firstElementChild.style.visibility = 'hidden';
      return;
    }

    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: 'hidden',
        transform: 'translateY(-100%)',
      },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.5,
        easing: [0.39, 0.575, 0.565, 1.0],
      }
    ).finished;
  }
}

if (!customElements.get('details-mega')) {
  customElements.define('details-mega', DetailsMega, { extends: 'details' });
}

class MenuDrawerDetails extends HTMLDetailsElement {
  constructor() {
    super();

    this.onSummaryClick = this.onSummaryClick.bind(this);
    this.onCloseButtonClick = this.onCloseButtonClick.bind(this);

    if (this.summary) {
      this.summary.addEventListener('click', this.onSummaryClick);
    }

    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.onCloseButtonClick);
    }
  }

  disconnectedCallback() {
    if (this.summary) {
      this.summary.removeEventListener('click', this.onSummaryClick);
    }

    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.onCloseButtonClick);
    }
  }

  get parent() {
    return this.closest('[data-parent]');
  }

  get summary() {
    return this.querySelector('summary');
  }

  get closeButton() {
    return this.querySelector('button');
  }

  onSummaryClick() {
    setTimeout(() => {
      if (this.parent) {
        this.parent.classList.add('active');
      }

      this.classList.add('active');

      if (this.summary) {
        this.summary.setAttribute('aria-expanded', 'true');
      }
    }, 100);
  }

  onCloseButtonClick() {
    if (this.parent) {
      this.parent.classList.remove('active');
    }

    this.classList.remove('active');

    if (this.summary) {
      this.summary.setAttribute('aria-expanded', 'false');
    }

    this.closeAnimation();
  }

  closeAnimation() {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        this.removeAttribute('open');
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

if (!customElements.get('menu-drawer-details')) {
  customElements.define('menu-drawer-details', MenuDrawerDetails, { extends: 'details' });
}

class MenuDrawer extends DrawerComponent {
  constructor() {
    super();
  }

  get header() {
    return this.closest('header');
  }

  get requiresBodyAppended() {
    return false;
  }

  prepareToShow() {
    super.prepareToShow();

    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);

    if (this.header) {
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom, 10)}px`
      );
    }

    document.dispatchEvent(new CustomEvent('menu-drawer:open', { bubbles: true }));
  }

  handleAfterHide() {
    super.handleAfterHide();

    setTimeout(() => {
      this.querySelectorAll('details[is=menu-drawer-details]').forEach((menu) => {
        if (typeof menu.onCloseButtonClick === 'function') {
          menu.onCloseButtonClick();
        }
      });
    });
  }
}

if (!customElements.get('menu-drawer')) {
  customElements.define('menu-drawer', MenuDrawer);
}

class MenuProductList extends HTMLElement {
  constructor() {
    super();

    this.slider = null;
    this.elementToFocus = this.querySelector('button');

    this.initSlide();
  }

  get container() {
    return this.querySelector('.swiper');
  }

  get sliderPagination() {
    return this.querySelector('.swiper-pagination');
  }

  get sliderNext() {
    return this.querySelector('.swiper-button-next');
  }

  get sliderPrev() {
    return this.querySelector('.swiper-button-prev');
  }

  get numberOfColumns() {
    const columns = parseInt(this.dataset.columns, 10);
    return Number.isFinite(columns) && columns > 0 ? columns : 1;
  }

  initSlide() {
    if (!this.container || !window.FoxTheme || !FoxTheme.Carousel) return;

    this.slider = new FoxTheme.Carousel(this.container, {
      spaceBetween: 10,
      slidesPerView: this.numberOfColumns,
      loop: false,
      navigation: {
        nextEl: this.sliderNext,
        prevEl: this.sliderPrev,
      },
      focusableElements: 'input, select, option, textarea, button, video, label, a',
    });

    this.slider.init();

    if (!FoxTheme.a11y || !FoxTheme.a11y.getFocusableElements) return;

    const focusableElements = FoxTheme.a11y.getFocusableElements(this);

    focusableElements.forEach((element) => {
      element.addEventListener('focusin', () => {
        const slide = element.closest('.swiper-slide');

        if (slide && this.slider && this.slider.slider) {
          this.slider.slider.slideTo(this.slider.slider.slides.indexOf(slide));
        }
      });
    });
  }

  calcNavButtonsPosition() {
    if (this.dataset.calcButtonPosition !== 'true') return;

    const firstMedia = this.querySelector('.product-card__image-wrapper');

    if (firstMedia && firstMedia.clientHeight > 0) {
      this.style.setProperty('--swiper-navigation-top-offset', `${parseInt(firstMedia.clientHeight, 10) / 2}px`);
    }
  }
}

if (!customElements.get('menu-product-list')) {
  customElements.define('menu-product-list', MenuProductList);
}

class MenuSidebar extends HTMLElement {
  constructor() {
    super();

    this.handleSidenavMenuToggle = this.handleSidenavMenuToggle.bind(this);
    this.handleSummaryClick = this.handleSummaryClick.bind(this);
    this.updateHeight = this.updateHeight.bind(this);

    this._observer = null;
  }

  get summarys() {
    return this.querySelectorAll('summary');
  }

  connectedCallback() {
    this.setInitialMinHeight();

    const firstSummary = this.summarys[0];

    if (firstSummary) {
      this.setActiveItem(firstSummary, false);
    }

    this.summarys.forEach((summary) => {
      summary.addEventListener('mouseenter', this.handleSidenavMenuToggle);
      summary.addEventListener('click', this.handleSummaryClick);
    });

    this.setupIntersectionObserver();
  }

  disconnectedCallback() {
    this.summarys.forEach((summary) => {
      summary.removeEventListener('mouseenter', this.handleSidenavMenuToggle);
      summary.removeEventListener('click', this.handleSummaryClick);
    });

    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  setInitialMinHeight() {
    const container = this.closest('.mega-menu__wrapper');

    if (container && container.parentElement) {
      const parentHeight = container.parentElement.offsetHeight || 0;
      container.style.setProperty('--menu-sidebar-height', `${parentHeight}px`);
    }
  }

  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      this.updateHeight();
      return;
    }

    this._observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.updateHeight();
        }
      });
    });

    this._observer.observe(this);
  }

  updateHeight() {
    const activeSummary = this.querySelector('.is-visible');

    if (!activeSummary) return;

    const container = this.closest('.mega-menu__wrapper');
    const content = activeSummary.nextElementSibling;

    if (container && content) {
      container.style.setProperty('--sidebar-height', `${content.scrollHeight}px`);
    }
  }

  setActiveItem(summaryElem, updateHeight = true) {
    const lastSidenavElem = this.querySelector('.is-visible');

    if (lastSidenavElem) {
      lastSidenavElem.classList.remove('is-visible');
    }

    summaryElem.classList.add('is-visible');

    if (updateHeight) {
      this.updateHeight();
    }
  }

  handleSidenavMenuToggle(evt) {
    const summaryElem = evt.target;

    if (summaryElem) {
      this.setActiveItem(summaryElem);
    }
  }

  handleSummaryClick(event) {
    event.preventDefault();
  }
}

if (!customElements.get('menu-sidebar')) {
  customElements.define('menu-sidebar', MenuSidebar);
}